import React, { useState } from 'react';

const SetPassword = ({ onSubmitPassword, loading, error, successMessage }) => {
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [passwordMatchError, setPasswordMatchError] = useState(''); // Only for password mismatch

    // --- Password Validation Checklists ---
    const passwordCriteria = {
        minLength: newPassword.length >= 8,
        hasUppercase: /[A-Z]/.test(newPassword),
        hasLowercase: /[a-z]/.test(newPassword),
        hasDigit: /[0-9]/.test(newPassword),
        hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(newPassword),
    };

    const isPasswordValid = Object.values(passwordCriteria).every(Boolean);

    const handleNewPasswordChange = (e) => {
        const value = e.target.value;
        setNewPassword(value);
        if (passwordMatchError) { // Clear mismatch error if user starts typing new password
            setPasswordMatchError('');
        }
    };

    const handleConfirmPasswordChange = (e) => {
        const value = e.target.value;
        setConfirmPassword(value);
        if (passwordMatchError) { // Clear mismatch error if user starts typing confirm password
            setPasswordMatchError('');
        }
    };

    const toggleShowNewPassword = () => {
        setShowNewPassword(!showNewPassword);
    };

    const toggleShowConfirmPassword = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Client-side validation
        if (!isPasswordValid) {
            // No specific error message needed here, as the checklist shows the issues
            return;
        }

        if (newPassword !== confirmPassword) {
            setPasswordMatchError('New password and confirm password do not match.');
            return;
        } else {
            setPasswordMatchError(''); // Clear if they now match
        }

        // If client-side validation passes, call the parent's onSubmitPassword prop
        if (onSubmitPassword) {
            onSubmitPassword(newPassword);
        }
    };

    return (
        <div style={styles.container}>
            <h1 style={styles.heading}>Set Your New Password</h1>
            <p style={styles.subheading}>
                Please enter and confirm your new password.
            </p>

            <form onSubmit={handleSubmit} style={styles.form}>
                {/* New Password Input */}
                <div style={styles.inputContainer}>
                    <input
                        type={showNewPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={handleNewPasswordChange}
                        placeholder="Enter New Password"
                        style={styles.input}
                        required
                        disabled={loading}
                    />
                    <button
                        type="button"
                        onClick={toggleShowNewPassword}
                        style={styles.toggleButton}
                        disabled={loading}
                    >
                        {showNewPassword ? 'Hide' : 'Show'}
                    </button>
                </div>

                {/* Password Strength Checklist */}
                <div style={styles.checklistContainer}>
                    <div style={styles.checklistRow}>
                        <input type="checkbox" checked={passwordCriteria.minLength} readOnly style={styles.checkbox} />
                        <label style={passwordCriteria.minLength ? styles.checklistMet : styles.checklistUnmet}>At least 8 characters</label>
                    </div>
                    <div style={styles.checklistRow}>
                        <input type="checkbox" checked={passwordCriteria.hasUppercase} readOnly style={styles.checkbox} />
                        <label style={passwordCriteria.hasUppercase ? styles.checklistMet : styles.checklistUnmet}>One uppercase letter</label>
                    </div>
                    <div style={styles.checklistRow}>
                        <input type="checkbox" checked={passwordCriteria.hasLowercase} readOnly style={styles.checkbox} />
                        <label style={passwordCriteria.hasLowercase ? styles.checklistMet : styles.checklistUnmet}>One lowercase letter</label>
                    </div>
                    <div style={styles.checklistRow}>
                        <input type="checkbox" checked={passwordCriteria.hasDigit} readOnly style={styles.checkbox} />
                        <label style={passwordCriteria.hasDigit ? styles.checklistMet : styles.checklistUnmet}>One digit (0-9)</label>
                    </div>
                    <div style={styles.checklistRow}>
                        <input type="checkbox" checked={passwordCriteria.hasSpecialChar} readOnly style={styles.checkbox} />
                        <label style={passwordCriteria.hasSpecialChar ? styles.checklistMet : styles.checklistUnmet}>One special character (!@#$...)</label>
                    </div>
                </div>

                {/* Confirm Password Input */}
                <div style={styles.inputContainer}>
                    <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={handleConfirmPasswordChange}
                        placeholder="Confirm New Password"
                        style={styles.input}
                        required
                        disabled={loading}
                    />
                    <button
                        type="button"
                        onClick={toggleShowConfirmPassword}
                        style={styles.toggleButton}
                        disabled={loading}
                    >
                        {showConfirmPassword ? 'Hide' : 'Show'}
                    </button>
                </div>

                {/* Error Messages */}
                {passwordMatchError && (
                    <p style={styles.errorMessage}>{passwordMatchError}</p>
                )}
                {error && ( // Server-side error from parent
                    <p style={styles.errorMessage}>{error}</p>
                )}
                {successMessage && ( // Success message from parent
                    <p style={styles.successMessage}>{successMessage}</p>
                )}

                <button
                    type="submit"
                    style={styles.button}
                    disabled={loading || successMessage || !isPasswordValid || newPassword !== confirmPassword}
                >
                    {loading ? 'Setting Password...' : 'Set Password'}
                </button>
            </form>
        </div>
    );
};

const styles = {
    // Green shades for consistency with previous component
    primaryGreen: '#4CAF50',
    darkGreen: '#388E3C',
    lightGreen: '#E8F5E9',
    accentGreen: 'rgba(76, 175, 80, 0.25)',
    successText: '#2e7d32', // Darker green for success messages
    errorText: '#d32f2f', // Red for errors

    container: {
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        padding: '40px',
        boxShadow: '0 8px 20px rgba(0, 0, 0, 0.08)',
        textAlign: 'center',
        maxWidth: '500px',
        margin: '40px auto',
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    },
    heading: {
        fontSize: '28px',
        fontWeight: '700',
        color: '#333',
        marginBottom: '15px',
        lineHeight: '1.3',
    },
    subheading: {
        fontSize: '16px',
        color: '#555',
        marginBottom: '30px',
        lineHeight: '1.5',
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '20px',
        marginBottom: '20px',
    },
    inputContainer: {
        position: 'relative',
        width: '100%',
        maxWidth: '350px',
        display: 'flex',
        alignItems: 'center',
    },
    input: {
        flexGrow: 1,
        padding: '12px 40px 12px 15px', // Adjusted padding for toggle button
        fontSize: '18px',
        border: '2px solid #ccc',
        borderRadius: '8px',
        outline: 'none',
        transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
        textAlign: 'left',
    },
    toggleButton: {
        position: 'absolute',
        right: '10px',
        top: '50%',
        transform: 'translateY(-50%)',
        backgroundColor: 'transparent',
        border: 'none',
        color: '#666',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '500',
        padding: '5px',
        transition: 'color 0.2s ease',
    },
    // --- Checklist Styles ---
    checklistContainer: {
        width: '100%',
        maxWidth: '350px',
        textAlign: 'left',
        marginTop: '-10px',
        marginBottom: '5px',
        paddingLeft: '5px',
    },
    checklistRow: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: '5px',
    },
    checkbox: {
        marginRight: '10px',
        width: '16px',
        height: '16px',
        // Make sure the checkbox itself is not interactive for readOnly
        cursor: 'default',
    },
    checklistMet: {
        color: "#4CAF50",
        fontWeight: '500',
    },
    checklistUnmet: {
        color: '#888',
        fontWeight: 'normal',
    },
    // --- End Checklist Styles ---
    button: {
        backgroundColor: "#4CAF50",
        color: '#fff',
        padding: '12px 25px',
        fontSize: '18px',
        fontWeight: '600',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        transition: 'background-color 0.3s ease, transform 0.2s ease',
        width: '100%',
        maxWidth: '250px',
        '&:disabled': { // This pseudo-class doesn't work directly in inline styles
            backgroundColor: '#a5d6a7', // Lighter green when disabled
            cursor: 'not-allowed',
        }
    },
    errorMessage: {
        color: "red",
        fontSize: '14px',
        marginTop: '-10px',
        marginBottom: '5px',
        width: '100%',
        maxWidth: '350px',
        textAlign: 'left',
    },
    successMessage: {
        color: "#4CAF50",
        fontSize: '16px',
        fontWeight: '600',
        marginTop: '-10px',
        marginBottom: '5px',
        width: '100%',
        maxWidth: '350px',
        textAlign: 'center',
    },
};

export default SetPassword;